/**
 * @fullvsl/contracts/mocks — Mock Full Funnel adapter + fixtures (Phase 0)
 *
 * Concrete no-credentials path: every package can build against this mock until
 * real Full Funnel credentials land. Swap-in is interface-identical (see contracts.ts
 * §11 `FullFunnelAdapter`). Per the autonomy brief, missing credentials NEVER block
 * player/analytics/integration work.
 */

import type {
  AnalyticsEvent,
  DispatchChannel,
  DispatchFanoutResult,
  DispatchResult,
  FfContact,
  FfOpportunity,
  FfTag,
  FfWorkflowTrigger,
  FFContext,
  FullFunnelAdapter,
  FullFunnelAdapterStatus,
  GhlWebhookEvent,
  ID,
  MediaResult,
  WebhookVerificationResult,
} from "./contracts";
import {
  DISPATCH_FANOUT_RULES,
  FULLFUNNEL_EVENT_MAP,
  MOCK_WEBHOOK_SECRET,
  GHL_WEBHOOK_SECRET_ENV,
} from "./contracts";

/** Confirmed CORS-enabled sample MP4 (PRD §7.1 / autonomy brief). */
export const FIXTURE_MP4 =
  "https://assets.cdn.filesafe.space/xqBFeOe8aUuZI8tfnSDv/media/67998dec4f0aeba88060946c.mp4";

export const FIXTURE_MEDIA: MediaResult = {
  url: FIXTURE_MP4,
  mimeType: "video/mp4",
  durationSec: 0, // probed at runtime by the player
  corsEnabled: true,
  rangeSupported: true,
};

/** Example decoded context (what parseContext returns for an empty blob). */
export const FIXTURE_CONTEXT: FFContext = {
  locationId: "fixture-location",
  contactId: "fixture-contact",
};

/** Fixture contact resolved by the mock when no real contact id is present. */
export const FIXTURE_CONTACT: FfContact = {
  id: "fixture-contact",
  locationId: "fixture-location",
  name: "Fixture Contact",
  email: "fixture@example.com",
};

/** A small, representative event stream for tests/demos. */
export const FIXTURE_EVENTS: AnalyticsEvent[] = [
  {
    eventId: "00000000-0000-4000-8000-000000000001",
    vslId: "vsl-fixture",
    sessionId: "sess-fixture",
    visitorId: "visitor-fixture",
    type: "impression",
    clientTs: "2026-07-02T08:00:00.000Z",
    meta: { source: "fullfunnel_media", device: "desktop", sourceMode: "html5", aspectRatio: "16:9" },
  },
  {
    eventId: "00000000-0000-4000-8000-000000000002",
    vslId: "vsl-fixture",
    sessionId: "sess-fixture",
    visitorId: "visitor-fixture",
    type: "play",
    clientTs: "2026-07-02T08:00:01.000Z",
    value: 0,
    meta: { positionSec: 0, pct: 0, source: "fullfunnel_media", device: "desktop", sourceMode: "html5" },
  },
  {
    eventId: "00000000-0000-4000-8000-000000000003",
    vslId: "vsl-fixture",
    sessionId: "sess-fixture",
    visitorId: "visitor-fixture",
    type: "milestone",
    clientTs: "2026-07-02T08:00:30.000Z",
    value: 25,
    meta: { milestone: 25, positionSec: 30, pct: 25, source: "fullfunnel_media", device: "desktop" },
  },
  {
    eventId: "00000000-0000-4000-8000-000000000004",
    vslId: "vsl-fixture",
    sessionId: "sess-fixture",
    visitorId: "visitor-fixture",
    type: "cta_shown",
    clientTs: "2026-07-02T08:01:00.000Z",
    meta: { positionSec: 60, pct: 50, revealMode: "percent", basedOn: "maxWatched", destinationType: "url" },
  },
  {
    eventId: "00000000-0000-4000-8000-000000000005",
    vslId: "vsl-fixture",
    sessionId: "sess-fixture",
    visitorId: "visitor-fixture",
    type: "cta_clicked",
    clientTs: "2026-07-02T08:01:05.000Z",
    meta: { positionSec: 65, pct: 54, revealMode: "percent", basedOn: "maxWatched", destinationType: "url" },
  },
];

/**
 * Mock Full Funnel adapter. Logs dispatched events; resolves the fixture media.
 * Drop-in replacement for the real adapter — same interface.
 *
 * Entity fan-out (FUL-15 / plan FUL-14 §5): `dispatchEventFanout` performs the
 * full multi-channel fan-out (tag → workflow → opportunity per
 * `DISPATCH_FANOUT_RULES`) and records each GHL entity in-memory so QA and the
 * §9 V5/V6 validation can assert exactly which entities an event produced.
 * `dispatchEvent` is the backward-compatible single-result convenience that
 * delegates to the fan-out. Dispatch is idempotent by `eventId`: a re-dispatch
 * of the same event re-applies nothing.
 *
 * Webhook path (FUL-51): signature verification is a shared-secret HMAC-SHA256
 * computed over the raw body. `emitRoundTripWebhook` simulates GHL echoing a
 * dispatched event back so the §9 V8 webhook round-trip has a concrete target
 * before real credentials land.
 */
export class MockFullFunnelAdapter implements FullFunnelAdapter {
  private scopes: string[];
  private dispatched: { event: AnalyticsEvent; mappedName: string }[] = [];

  // FUL-15 entity stores (inspectable; mirror what a real GHL account would hold).
  private readonly tags = new Map<string, FfTag>();
  private readonly workflows: FfWorkflowTrigger[] = [];
  private readonly opportunities: FfOpportunity[] = [];
  private readonly contacts = new Map<string, FfContact>();
  /** Full fan-out log (one entry per distinct dispatched eventId). */
  private readonly fanoutLog: DispatchFanoutResult[] = [];
  /** eventIds already dispatched → idempotent re-dispatch is a no-op. */
  private readonly dispatchedEventIds = new Set<ID>();

  constructor(scopes: string[] = ["media", "contacts", "tags", "workflows", "opportunities"]) {
    this.scopes = scopes;
  }

  parseContext(query: string | Record<string, unknown>): FFContext {
    if (!query) return { ...FIXTURE_CONTEXT };
    if (typeof query === "string") {
      try {
        // Isomorphic base64 decode: Node Buffer when present, else browser atob.
        const g = globalThis as { Buffer?: { from: (s: string, enc: string) => { toString: (enc: string) => string } } };
        const decoded = g.Buffer ? g.Buffer.from(query, "base64").toString("utf8") : atob(query);
        const parsed = JSON.parse(decoded) as Record<string, unknown>;
        return { raw: parsed, locationId: parsed.locationId as string | undefined };
      } catch {
        return { raw: { rawQuery: query } };
      }
    }
    return { raw: query, locationId: query.locationId as string | undefined };
  }

  resolveMedia(_locationId?: string, _query?: Record<string, unknown>): MediaResult {
    return { ...FIXTURE_MEDIA };
  }

  /**
   * Resolve a contact from context (FUL-15). Falls back to the fixture contact;
   * a real adapter calls `contacts.read`. Idempotent: the same contactId always
   * resolves to the same recorded contact.
   */
  resolveContact(context: FFContext): FfContact {
    const id = context.contactId ?? FIXTURE_CONTACT.id;
    const existing = this.contacts.get(id);
    if (existing) return { ...existing };
    const contact: FfContact = {
      id,
      locationId: context.locationId ?? FIXTURE_CONTACT.locationId,
      name: context.name ?? (id === FIXTURE_CONTACT.id ? FIXTURE_CONTACT.name : undefined),
      email: context.email,
      phone: context.phone,
    };
    this.contacts.set(id, contact);
    return { ...contact };
  }

  /** Backward-compatible single-result dispatch; delegates to the full fan-out. */
  dispatchEvent(event: AnalyticsEvent, mapping: Record<string, string>): DispatchResult {
    const fanout = this.dispatchEventFanout(event, mapping);
    // Keep the legacy log shape in sync for `dispatchedEvents` consumers.
    this.dispatched.push({ event, mappedName: fanout.mappedName });
    console.log(`[MockFullFunnelAdapter] dispatch → ${fanout.mappedName} [${fanout.channels.join(",")}]`, event);
    // Aggregate result: the first channel that fired, or a tag placeholder when
    // the event fans out to nothing (e.g. progress/seek). Always ok + mock.
    const primary = fanout.results[0] ?? {
      ok: true,
      channel: "tag" as DispatchChannel,
      externalId: `mock_${event.eventId}`,
      mock: true,
    };
    return primary;
  }

  /**
   * Full multi-channel fan-out (FUL-15). Computes the mapped name, resolves the
   * contact, and applies tag / workflow / opportunity per DISPATCH_FANOUT_RULES,
   * recording each entity. Idempotent by eventId: a re-dispatch returns the
   * prior fan-out result without re-applying any entity.
   */
  dispatchEventFanout(event: AnalyticsEvent, mapping: Record<string, string>): DispatchFanoutResult {
    const prior = this.fanoutLog.find((f) => f.eventId === event.eventId);
    if (prior) return clone(prior);

    const mappedName = this.mappedNameFor(event, mapping);
    const channels = (DISPATCH_FANOUT_RULES[event.type] ?? []).slice();
    const contact = this.resolveContact(this.contextForEvent(event));
    const results: DispatchResult[] = [];
    const out: DispatchFanoutResult = {
      eventId: event.eventId,
      mappedName,
      channels,
      results,
      contact: { ...contact },
    };

    const now = new Date().toISOString();
    for (const channel of channels) {
      if (channel === "tag") {
        const tag: FfTag = {
          id: `${contact.id}:${mappedName}`,
          name: mappedName,
          contactId: contact.id,
          locationId: contact.locationId,
          sourceEventId: event.eventId,
          appliedAt: now,
        };
        // Idempotent tag application (dedupe by {contact, name}).
        this.tags.set(tag.id, tag);
        out.tag = { ...tag };
        results.push({ ok: true, channel: "tag", externalId: tag.id, mock: true });
      } else if (channel === "workflow") {
        const wf: FfWorkflowTrigger = {
          id: `wf_${event.eventId}`,
          workflowName: mappedName,
          contactId: contact.id,
          locationId: contact.locationId,
          sourceEventId: event.eventId,
          triggeredAt: now,
        };
        this.workflows.push(wf);
        out.workflow = { ...wf };
        results.push({ ok: true, channel: "workflow", externalId: wf.id, mock: true });
      } else if (channel === "opportunity") {
        const opp: FfOpportunity = {
          id: `opp_${event.eventId}`,
          name: `FullVSL CTA — ${mappedName}`,
          contactId: contact.id,
          locationId: contact.locationId,
          pipelineStage: "new",
          sourceEventId: event.eventId,
          createdAt: now,
        };
        this.opportunities.push(opp);
        out.opportunity = { ...opp };
        results.push({ ok: true, channel: "opportunity", externalId: opp.id, mock: true });
      }
      // "webhook" is the inbound §12 path; outbound echo is emitRoundTripWebhook.
    }

    this.dispatchedEventIds.add(event.eventId);
    this.fanoutLog.push(clone(out));
    return out;
  }

  /** Map an analytics event type to its GHL name (milestone → watched_<pct>). */
  private mappedNameFor(event: AnalyticsEvent, mapping: Record<string, string>): string {
    const base = mapping[event.type] ?? FULLFUNNEL_EVENT_MAP[event.type];
    if (base) {
      // Milestones carry the percent; the map keeps the unsuffixed token so we
      // suffix here to produce fullvsl_watched_25 / _50 / _75 / _100.
      if (event.type === "milestone" && event.value != null) {
        return `fullvsl_watched_${event.value}`;
      }
      return base;
    }
    return `fullvsl_${event.type}`;
  }

  /** Build the FFContext used to resolve the contact for an analytics event. */
  private contextForEvent(event: AnalyticsEvent): FFContext {
    const meta = (event.meta ?? {}) as Record<string, unknown>;
    return {
      contactId: meta.contactId as string | undefined,
      locationId: meta.locationId as string | undefined,
    };
  }

  getStatus(): FullFunnelAdapterStatus {
    return { live: false, scopes: this.scopes, reason: "no credentials — mock mode" };
  }

  /** Test helper: events dispatched through the legacy single-result path. */
  get dispatchedEvents() {
    return [...this.dispatched];
  }

  /* ---------------------------------------------------------------------------------
   * FUL-15 inspection getters (QA / §9 V5–V6 validation assert against these).
   * --------------------------------------------------------------------------------- */

  /** Tags applied to contacts, keyed idempotently by `{contactId}:{name}`. */
  get appliedTags(): FfTag[] {
    return [...this.tags.values()].map((t) => ({ ...t }));
  }
  /** Workflow triggers fired by dispatched events (insertion-ordered). */
  get triggeredWorkflows(): FfWorkflowTrigger[] {
    return this.workflows.map((w) => ({ ...w }));
  }
  /** Opportunities created as the conversion proxy (cta_clicked). */
  get createdOpportunities(): FfOpportunity[] {
    return this.opportunities.map((o) => ({ ...o }));
  }
  /** Contacts resolved by `resolveContact` (keyed by contactId). */
  get resolvedContacts(): FfContact[] {
    return [...this.contacts.values()].map((c) => ({ ...c }));
  }
  /** Full fan-out log: one entry per distinct dispatched eventId. */
  get dispatchFanoutLog(): DispatchFanoutResult[] {
    return this.fanoutLog.map((f) => clone(f));
  }

  /* ---------------------------------------------------------------------------------
   * Webhook receiver (FUL-51)
   * --------------------------------------------------------------------------------- */

  /** The shared secret used for mock signature verification. */
  private get webhookSecret(): string {
    const g = globalThis as { process?: { env?: Record<string, string | undefined> } };
    return g.process?.env?.[GHL_WEBHOOK_SECRET_ENV] ?? MOCK_WEBHOOK_SECRET;
  }

  async verifyWebhookSignature(
    rawBody: string,
    signature: string | undefined,
  ): Promise<WebhookVerificationResult> {
    if (!signature) return { ok: false, reason: "missing_signature" };
    const expected = await hmacSha256Hex(this.webhookSecret, rawBody);
    // Constant-time-ish comparison to avoid timing leaks on the real path.
    if (expected.length !== signature.length || !timingSafeEqualHex(expected, signature)) {
      return { ok: false, reason: "bad_signature" };
    }
    return { ok: true };
  }

  async emitRoundTripWebhook(
    event: AnalyticsEvent,
  ): Promise<{ event: GhlWebhookEvent; signature: string }> {
    const mappedName =
      FULLFUNNEL_EVENT_MAP[event.type] ?? `fullvsl_${event.type}`;
    const payload: Record<string, unknown> = {
      type: mappedName,
      sourceEventId: event.eventId,
      vslId: event.vslId,
      sessionId: event.sessionId,
      visitorId: event.visitorId,
      value: event.value,
      meta: event.meta,
      // Synthetic GHL-style fields so the recorded event resembles a real echo.
      locationId: event.meta?.locationId ?? "fixture-location",
      contactId: event.meta?.contactId,
      emittedAt: event.clientTs,
    };
    const ghlEvent: GhlWebhookEvent = {
      id: `ghl-echo-${event.eventId}`,
      type: mappedName,
      locationId: payload.locationId as string | undefined,
      contactId: payload.contactId as string | undefined,
      sourceEventId: event.eventId,
      payload,
      emittedAt: event.clientTs,
    };
    const rawBody = JSON.stringify(payload);
    const signature = await hmacSha256Hex(this.webhookSecret, rawBody);
    return { event: ghlEvent, signature };
  }
}

/* =====================================================================================
 * HMAC helpers — isomorphic Web Crypto (Node 18+ and modern browsers).
 * ===================================================================================== */

/** Structural deep clone for plain-data entities (no Dates/Maps in payloads). */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

async function hmacSha256Hex(secret: string, body: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await globalThis.crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await globalThis.crypto.subtle.sign("HMAC", key, enc.encode(body));
  return [...new Uint8Array(sig)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Constant-time-ish hex string comparison (length-equal inputs). */
function timingSafeEqualHex(a: string, b: string): boolean {
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
